import type { CallState } from "@vd/rules/labels/views";
import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export type { CallState };

export const badgeVariants = cva(
  "inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-pill px-2 font-medium text-micro [&_svg]:size-3",
  {
    variants: {
      tone: {
        neutral: "bg-fill-2 text-ink-2",
        accent: "bg-accent-soft text-accent-ink",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger-ink",
        outline: "text-ink-2 shadow-[inset_0_0_0_1px_var(--vd-hairline-strong)]",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  icon?: ReactNode;
}

/** Small label for counts, tags and stages. Not for call state; use StatusPill. */
export function Badge({ tone, icon, className, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {icon}
      {children}
    </span>
  );
}

const STATE_STYLE: Record<
  CallState,
  { pill: string; dot: string; label: string; pulse?: boolean }
> = {
  idle: { pill: "bg-fill-2 text-ink-2", dot: "bg-ink-3", label: "Ready" },
  dialing: {
    pill: "bg-ringing-soft text-ringing",
    dot: "bg-ringing",
    label: "Dialing",
    pulse: true,
  },
  ringing: {
    pill: "bg-ringing-soft text-ringing",
    dot: "bg-ringing",
    label: "Ringing",
    pulse: true,
  },
  live: { pill: "bg-live-soft text-live", dot: "bg-live", label: "Live" },
  hold: { pill: "bg-hold-soft text-hold", dot: "bg-hold", label: "On hold", pulse: true },
  muted: { pill: "bg-muted-soft text-muted", dot: "bg-muted", label: "Muted" },
  ended: { pill: "bg-ended-soft text-ended", dot: "bg-ended", label: "Ended" },
  dropped: { pill: "bg-dropped-soft text-dropped", dot: "bg-dropped", label: "Dropped" },
  recording: { pill: "bg-dropped-soft text-ink", dot: "bg-recording", label: "Recording" },
};

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  state: CallState;
  /** Override the default label (for example "Ringing 0:12"). */
  children?: ReactNode;
  size?: "sm" | "md";
}

/**
 * Call state, always color plus text, never color alone. Ringing, dialing and hold pulse
 * their dot (attention); live is steady; recording blinks slowly.
 */
export function StatusPill({ state, size = "md", className, children, ...props }: StatusPillProps) {
  const s = STATE_STYLE[state];
  return (
    <span
      data-state={state}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill font-medium tnum",
        size === "sm" ? "h-5 px-2 text-micro" : "h-6 px-2.5 text-caption",
        s.pill,
        className,
      )}
      {...props}
    >
      <StatusDot state={state} pulse={s.pulse} />
      {children ?? s.label}
    </span>
  );
}

export function StatusDot({
  state,
  pulse,
  className,
}: {
  state: CallState;
  pulse?: boolean;
  className?: string;
}) {
  const s = STATE_STYLE[state];
  const animate = pulse ?? s.pulse;
  return (
    <span aria-hidden className={cn("relative inline-flex size-1.5 shrink-0", className)}>
      {animate ? (
        <span className={cn("absolute inset-0 animate-vd-pulse rounded-pill", s.dot)} />
      ) : null}
      <span
        className={cn(
          "relative inline-flex size-1.5 rounded-pill",
          s.dot,
          state === "recording" && "animate-vd-rec",
        )}
      />
    </span>
  );
}
